// Campaign: its creatives and distributions, tenant pause/resume (never
// lifts platform or billing holds) and archive.
import type { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Archive, Pause, Pencil, Play, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { keys, useFrontDesk } from "@/api/hooks";
import type { Creative, Distribution } from "@/api/types";
import { PRODUCTS, displayFormat } from "@/lib/labels";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { ConfirmDialog } from "@/ui/dialog";
import { Callout, EmptyState, PageHeader, Section } from "@/ui/layout";
import { DataTable } from "@/components/data-table";
import { NameDialog } from "@/components/name-dialog";
import { ServingStateBadge } from "@/components/status";
import { Breadcrumbs } from "../common/breadcrumbs";
import { notifyError } from "../common/notify";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";
import { useCampaign, useCampaignDistributions, useCreatives } from "../common/queries";
import { CampaignAuthorization } from "../commercial/campaign-authorization";

export function CampaignDetailPage() {
  const { orgId, campaignId } = useRouteParams("orgId", "campaignId");
  const queryClient = useQueryClient();
  const { data: fd } = useFrontDesk(orgId);
  const campaign = useCampaign(orgId, campaignId);
  const creatives = useCreatives(orgId, campaignId);
  const distributions = useCampaignDistributions(orgId, campaignId);
  const [pending, setPending] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  useTitle(campaign.data?.name);
  if (campaign.isLoading) return <PageLoading />;
  if (campaign.error || !campaign.data) return <PageError error={campaign.error} orgId={orgId} />;
  const c = campaign.data;
  const base = `/o/${orgId}`;
  const canManage = (fd?.capabilities.manageCampaigns ?? false) && !c.archived;
  const path = { params: { path: { orgId, campaignId } } };
  const act = async (run: () => Promise<unknown>, success: string) => {
    setPending(true);
    try {
      await run();
      toast.success(success);
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setPending(false);
      setArchiveOpen(false);
    }
  };
  const creativeColumns: Array<ColumnDef<Creative>> = [
    {
      header: "Creativo",
      cell: ({ row }) => (
        <Link to={`${base}/creatives/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    { header: "Tipo", cell: ({ row }) => PRODUCTS[row.original.product].label },
  ];
  const distributionColumns: Array<ColumnDef<Distribution>> = [
    {
      header: "Distribución",
      cell: ({ row }) => (
        <Link to={`${base}/distributions/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    {
      header: "Dónde se usa",
      cell: ({ row }) => `${row.original.platform.name} · ${displayFormat(row.original.format)}`,
    },
    { header: "Estado", cell: ({ row }) => <ServingStateBadge state={row.original.serving.effectiveState} /> },
  ];
  return (
    <div className="grid gap-6">
      <Breadcrumbs orgId={orgId} clientId={c.clientId} brandId={c.brandId} current={c.name} />
      <PageHeader
        eyebrow="Campaña"
        title={
          <span className="flex items-center gap-2">
            {c.name} {c.archived ? <Badge>Archivada</Badge> : c.paused ? <Badge tone="warning">Pausada</Badge> : null}
          </span>
        }
        actions={
          <>
            {fd?.capabilities.createCreative && !c.archived ? (
              <Button asChild variant="primary" size="sm">
                <Link to={`${base}/create`} search={{ campaignId }}>
                  <Plus /> Crear creativo aquí
                </Link>
              </Button>
            ) : null}
            {canManage ? (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    setRenameOpen(true);
                  }}
                >
                  <Pencil /> Renombrar
                </Button>
                {c.paused ? (
                  <Button
                    size="sm"
                    disabled={pending}
                    onClick={() =>
                      void act(
                        () =>
                          call(() => api.POST("/orgs/{orgId}/campaigns/{campaignId}/resume", { ...path, body: {} })),
                        "Campaña reanudada"
                      )
                    }
                  >
                    <Play /> Reanudar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={pending}
                    onClick={() =>
                      void act(
                        () => call(() => api.POST("/orgs/{orgId}/campaigns/{campaignId}/pause", { ...path, body: {} })),
                        "Campaña pausada"
                      )
                    }
                  >
                    <Pause /> Pausar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="danger-outline"
                  onClick={() => {
                    setArchiveOpen(true);
                  }}
                >
                  <Archive /> Archivar
                </Button>
              </>
            ) : null}
          </>
        }
      />
      {c.paused ? (
        <Callout tone="warning">
          La campaña está pausada por tu equipo: sus distribuciones no se entregan hasta reanudarla.
        </Callout>
      ) : null}
      <CampaignAuthorization orgId={orgId} campaignId={campaignId} />
      <Section title="Creatividades">
        <DataTable
          label="Creatividades"
          columns={creativeColumns}
          rows={creatives.items}
          loading={creatives.isLoading}
          hasMore={creatives.hasNextPage}
          loadingMore={creatives.isFetchingNextPage}
          onLoadMore={() => void creatives.fetchNextPage()}
          empty={<EmptyState title="Sin creatividades" />}
        />
      </Section>
      <Section title="Distribuciones">
        <DataTable
          label="Distribuciones"
          columns={distributionColumns}
          rows={distributions.items}
          loading={distributions.isLoading}
          hasMore={distributions.hasNextPage}
          loadingMore={distributions.isFetchingNextPage}
          onLoadMore={() => void distributions.fetchNextPage()}
          empty={<EmptyState title="Sin distribuciones" />}
        />
      </Section>
      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Renombrar campaña"
        label="Nombre"
        initial={c.name}
        submitLabel="Guardar"
        onSubmit={async (name) => {
          await call(() => api.PATCH("/orgs/{orgId}/campaigns/{campaignId}", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="¿Archivar esta campaña?"
        description="No podrás crear ni publicar nada nuevo en ella. Es definitivo; su historial se conserva."
        confirmLabel="Archivar"
        destructive
        pending={pending}
        onConfirm={() =>
          void act(
            () => call(() => api.POST("/orgs/{orgId}/campaigns/{campaignId}/archive", path)),
            "Campaña archivada"
          )
        }
      />
    </div>
  );
}
